'use client';
import { useEffect, useState } from 'react';

import { Skeleton } from '@mantine/core';
import { SRStatus, SupplierRequest } from '@scaleup/models';
import {
  useGetImportedSupplierRequestsQuery,
  useGetSupplierRequestsQuery,
} from '../../api';
import { Loading } from '../../ui';
import { SupplierRequestListCard } from './components';

export const SupplierRequestLists = ({
  filter,
}: {
  filter?: 'active' | 'previous' | 'imported';
}) => {
  const { data: supplierRequests, isLoading: supplierRequestsLoading } =
    useGetSupplierRequestsQuery(undefined, { pollingInterval: 60_000 }); // poll every 60 seconds
  const {
    data: importedSupplierRequests,
    isFetching: importedSupplierRequestsLoading,
  } = useGetImportedSupplierRequestsQuery();

  const [filteredSupplierRequests, setFilteredSupplierRequests] = useState<
    SupplierRequest[]
  >([]);

  useEffect(() => {
    if (filter && supplierRequests) {
      switch (filter) {
        case 'active':
          setFilteredSupplierRequests(
            supplierRequests.filter(
              (request: SupplierRequest) => request.status === SRStatus.ACTIVE
            ) ?? []
          );
          break;
        case 'previous':
          setFilteredSupplierRequests(
            supplierRequests.filter(
              (request: SupplierRequest) => request.status === SRStatus.ARCHIVED
            ) ?? []
          );
          break;
        case 'imported':
          setFilteredSupplierRequests([
            ...(importedSupplierRequests || []),
            ...supplierRequests.filter(
              (request: SupplierRequest) => request.status === SRStatus.IMPORTED
            ),
          ]);
          break;
        default:
          setFilteredSupplierRequests(
            supplierRequests.filter(
              (request: any) => request.status !== SRStatus.DRAFT
            ) ?? []
          );
          break;
      }
    }
  }, [filter, supplierRequests]);

  const generatingDocs = ({ documents }: SupplierRequest) =>
    documents &&
    documents.length > 0 && (
      <div>
        <div className="w-full flex justify-center items-center gap-2 py-4">
          <span className="text-xl w-full font-bold text-text-primary">
            {`Generating Supplier Request from ${documents[0]?.fileName}`}
          </span>
        </div>
      </div>
    );

  return (
    <Loading
      skeleton={
        <>
          <Skeleton className="rounded-[2rem]" height={350} my={10} />
          <Skeleton className="rounded-[2rem]" height={350} my={10} />
          <Skeleton className="rounded-[2rem]" height={350} my={10} />
          <Skeleton className="rounded-[2rem]" height={350} my={10} />
          <Skeleton className="rounded-[2rem]" height={350} my={10} />
          <Skeleton className="rounded-[2rem]" height={350} my={10} />
        </>
      }
      loading={supplierRequestsLoading || importedSupplierRequestsLoading}
    >
      {filteredSupplierRequests.map((supplierRequest) => {
        return (
          <div
            key={supplierRequest.requestId}
            className="flex-1 rounded-[2rem] bg-white p-8"
          >
            {supplierRequest.status === SRStatus.GENERATING_FROM_DOC ? (
              generatingDocs(supplierRequest)
            ) : (
              <SupplierRequestListCard supplierRequest={supplierRequest} />
            )}
          </div>
        );
      })}
    </Loading>
  );
};
